import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import FolderSelector from 'file-browser/FolderSelector';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import * as RegistryUtil from 'registry/RegistryUtil';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import waitForEntrySaved from '../utils/waitForEntrySaved';
import * as AutomationFileDAO from './AutomationFileDAO';

interface AutomationOutputFileFolderPickerProps {
  runId: string | null;
  automationOutputFile: AutomationOutputFileGraphQL;
  locked: boolean;
  updateAutomationOutputFile: (automationOutputFile: AutomationOutputFileGraphQL) => void;
}

interface ConnectedProps {
  outputFileStatus: AutomationFileStatusGraphQL | null;
}

class AutomationOutputFileFolderPickerConnector extends DataConnector<
  AutomationOutputFileFolderPickerProps,
  ConnectedProps
> {
  enhanceProps(): ConnectedProps {
    return {
      outputFileStatus: this.props.automationOutputFile.id
        ? this.reduxState().ui.automationFileStatus[this.props.automationOutputFile.id]
        : null,
    };
  }
}

const AutomationOutputFileFolderPicker = (
  props: AutomationOutputFileFolderPickerProps & ConnectedProps
): JSX.Element | null => {
  const automationOutputFile = props.automationOutputFile;
  const locked =
    props.locked ||
    props.outputFileStatus === AutomationFileStatusGraphQL.running ||
    automationOutputFile.status !== AutomationFileStatusGraphQL.pending;

  return (
    <Row className="u-mini-spacing-top" bufferRowReset={true}>
      <Col md={3}>
        <span className="fieldLabel-sublabel">Folder</span>
      </Col>
      <Col md={9}>
        <EditorPropertiesContext.Consumer>
          {({fileId}) => (
            <FolderSelector
              buttonSize={ButtonSize.MINI}
              enableRedesign={true}
              showInventory={true}
              showRegistries={!BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES}
              readOnly={locked}
              value={automationOutputFile.folderId}
              onChange={async (e: {target: {value: string}}) => {
                const saved = fileId != null ? await waitForEntrySaved(fileId) : true;
                if (saved) {
                  const {folderId} = RegistryUtil.parseFolderValue(e.target.value);
                  const response = await AutomationFileDAO.updateAutomationOutputFileParams(
                    automationOutputFile.id,
                    {folderId}
                  );
                  props.updateAutomationOutputFile(response.automationOutputFile);
                }
              }}
              unsetOnFetchFail={true}
            />
          )}
        </EditorPropertiesContext.Consumer>
      </Col>
    </Row>
  );
};

export default AutomationOutputFileFolderPickerConnector.connect(AutomationOutputFileFolderPicker);
